import { useValidatedCubeQuery } from "app/hooks/cube";
import { createCombinedError } from "app/hooks/cube/utils";
import { type FilterBarCubeFilters } from "app/views/dashboards/utils/costDashboardFilterBar";

import { hasAccountFilters } from "./filterTransformUtils";
import {
  costSavingOpportunityTagResultArraySchema,
  type CostSavingOpportunityTagResult,
} from "./useCostSavingOpportunityTag.schema";

import type { Query, Filter, QueryOrder } from "@cubejs-client/core";
import type { Range } from "types/cube";

const CUBE_NAME = "CSOTag" as const;

/** Props for the useCostSavingOpportunityTag hook */
interface UseCostSavingOpportunityTagProps {
  /** Dashboard key to filter by */
  dashboardKey: string;
  /** Date range for the query */
  dateRange: Range;
  /** Optional section key to filter by */
  sectionKey?: string;
  /** Optional resource tag key to filter by */
  resourceTagKey?: string;
  /** Optional resource tag value to filter by */
  resourceTagValue?: string;
  /** Optional additional filters to apply */
  filters?: FilterBarCubeFilters;
  /** Whether to include dashboard summary segment for aggregated costs */
  dashboardSummary?: boolean;
  /** Whether to include section_key as a dimension for grouping */
  includeSectionDimension?: boolean;
  /** Optional custom order for the query results */
  order?: Record<string, QueryOrder>;
  /** Whether the query is enabled */
  enabled?: boolean;
}

/** Props for the useCostSavingOpportunityTag hook result */
interface UseCostSavingOpportunityTagResult {
  /** Validated cost saving opportunity tag data */
  data: CostSavingOpportunityTagResult[] | null;
  /** Whether the query is loading */
  isLoading: boolean;
  /** Combined error from query and validation */
  error: Error | null;
}

function buildQuery(
  dashboardKey: string,
  dateRange: Range,
  sectionKey?: string,
  resourceTagKey?: string,
  resourceTagValue?: string,
  filters?: FilterBarCubeFilters,
  dashboardSummary?: boolean,
  includeSectionDimension?: boolean,
  order?: Record<string, QueryOrder>,
): Query {
  // Include Account dimensions if filters contain account-related filters
  const includeAccountDimensions = hasAccountFilters(filters);

  return {
    measures: [
      `${CUBE_NAME}.monthlyListSavingOpportunity`,
      `${CUBE_NAME}.monthlyBilledSavingOpportunity`,
      `${CUBE_NAME}.monthlyEffectiveSavingOpportunity`,
      `${CUBE_NAME}.nonCompliantResourceCount`,
    ],
    dimensions: [
      `${CUBE_NAME}.dashboard_key`,
      `${CUBE_NAME}.resource_tag_key`,
      `${CUBE_NAME}.resource_tag_value`,
      ...(sectionKey || includeSectionDimension
        ? [`${CUBE_NAME}.section_key`]
        : []),
      ...(includeAccountDimensions
        ? ["Account.key", "Account.name", "Account.email", "Account.groups"]
        : []),
    ],
    segments: dashboardSummary && !sectionKey ? [`${CUBE_NAME}.summary`] : [],
    timeDimensions: [
      {
        dimension: `${CUBE_NAME}.date`,
        granularity: "day",
        dateRange,
      },
    ],
    filters: [
      {
        member: `${CUBE_NAME}.dashboard_key`,
        operator: "equals" as const,
        values: [dashboardKey],
      },
      ...(sectionKey
        ? [
            {
              member: `${CUBE_NAME}.section_key`,
              operator: "equals" as const,
              values: [sectionKey],
            } as Filter,
          ]
        : []),
      ...(resourceTagKey
        ? [
            {
              member: `${CUBE_NAME}.resource_tag_key`,
              operator: "equals" as const,
              values: [resourceTagKey],
            } as Filter,
          ]
        : []),
      ...(resourceTagValue
        ? [
            {
              member: `${CUBE_NAME}.resource_tag_value`,
              operator: "equals" as const,
              values: [resourceTagValue],
            } as Filter,
          ]
        : []),
      ...(filters ?? []),
    ],
    order: order ?? {
      [`${CUBE_NAME}.date`]: "desc",
    },
  };
}

export function useCostSavingOpportunityTag({
  dashboardKey,
  dateRange,
  sectionKey,
  resourceTagKey,
  resourceTagValue,
  filters,
  dashboardSummary = true,
  includeSectionDimension = false,
  order,
  enabled = true,
}: UseCostSavingOpportunityTagProps): UseCostSavingOpportunityTagResult {
  const { validatedRawData, isLoading, error, validationError } =
    useValidatedCubeQuery(
      buildQuery(
        dashboardKey,
        dateRange,
        sectionKey,
        resourceTagKey,
        resourceTagValue,
        filters,
        dashboardSummary,
        includeSectionDimension,
        order,
      ),
      costSavingOpportunityTagResultArraySchema,
      { skip: !enabled },
    );

  const combinedError = createCombinedError(
    { error, validationError },
    { errorPrefix: "Cost saving opportunity tag query validation failed" },
  );

  return {
    data: validatedRawData ?? null,
    isLoading,
    error: combinedError,
  };
}
